import { useQuery } from '@apollo/client';
import { GET_METRIC_FILTER_DATA } from '@frontend/data-access';
import { Select } from 'antd';
import useTranslation from 'next-translate/useTranslation';

interface MetricsDashboardFiltersProps {
  channelId?: string;
  projectId?: string;
  challengeId?: string;
  topicId?: string;
  businessArea?: string;
  countryId?: string;
  onChangeFilter: ({
    channelId,
    projectId,
    challengeId,
    topicId,
    businessArea,
    countryId,
  }: {
    channelId?: string;
    projectId?: string;
    challengeId?: string;
    topicId?: string;
    businessArea?: string;
    countryId?: string;
  }) => void;
}

function MetricsDashboardFilters({
  channelId,
  projectId,
  challengeId,
  topicId,
  businessArea,
  countryId,
  onChangeFilter,
}: MetricsDashboardFiltersProps) {
  const { t } = useTranslation('common');
  const { data, loading } = useQuery<{
    dashboardMetricFilterData: {
      channels: { id: string; title: string }[];
      projects: { id: string; name: string }[];
      challenges: { id: string; title: string }[];
      topics: { id: string; name: string }[];
      businessAreas: string[];
      countries: { id: string; name: string }[];
    };
  }>(GET_METRIC_FILTER_DATA);

  return (
    <div className="flex flex-wrap gap-4 bg-gray-100 p-4 rounded-2xl items-center">
      <div className="flex flex-col gap-2">
        <label className="text-sm text-gray-900">
          {t('select_idea_channel')}
        </label>
        <Select
          defaultValue="ALL"
          className="!w-[200px]"
          placeholder={t('select_idea_channel')}
          options={[
            { label: t('all'), value: 'ALL' },
            ...(data?.dashboardMetricFilterData.channels.map((channel) => ({
              label: channel.title,
              value: channel.id,
            })) || []),
          ]}
          onChange={(value) => onChangeFilter({ channelId: value })}
        />
      </div>

      <div className="flex flex-col gap-2">
        <label className="text-sm text-gray-900">{t('project')}</label>
        <Select
          defaultValue="ALL"
          className="!w-[200px]"
          placeholder={t('project')}
          options={[
            { label: t('all'), value: 'ALL' },
            ...(data?.dashboardMetricFilterData.projects.map((project) => ({
              label: project.name,
              value: project.id,
            })) || []),
          ]}
          onChange={(value) => onChangeFilter({ projectId: value })}
        />
      </div>

      <div className="flex flex-col gap-2">
        <label className="text-sm text-gray-900">{t('challenge')}</label>
        <Select
          defaultValue="ALL"
          placeholder={t('select_a_challenge')}
          optionFilterProp="label"
          className="!w-[200px]"
          options={[
            { label: t('all'), value: 'ALL' },
            ...(data?.dashboardMetricFilterData.challenges.map((challenge) => ({
              label: challenge.title,
              value: challenge.id,
            })) || []),
          ]}
          onChange={(value) => onChangeFilter({ challengeId: value })}
        />
      </div>

      <div className="flex flex-col gap-2">
        <label className="text-sm text-gray-900">{t('topics')}</label>
        <Select
          defaultValue="ALL"
          className="!w-[200px]"
          maxTagCount="responsive"
          placeholder={t('select_topics')}
          options={[
            { label: t('all'), value: 'ALL' },
            ...(data?.dashboardMetricFilterData.topics.map((topic) => ({
              label: topic.name,
              value: topic.id,
            })) || []),
          ]}
          onChange={(value) => onChangeFilter({ topicId: value })}
        />
      </div>

      <div className="flex flex-col gap-2">
        <label className="text-sm text-gray-900">{t('b_and_f_area')}</label>
        <Select
          defaultValue="ALL"
          className="!w-[200px]"
          placeholder={t('b_and_f_area')}
          options={[
            { label: t('all'), value: 'ALL' },
            ...(data?.dashboardMetricFilterData.businessAreas.map((area) => ({
              label: area,
              value: area,
            })) || []),
          ]}
          onChange={(value) => onChangeFilter({ businessArea: value })}
        />
      </div>

      <div className="flex flex-col gap-2">
        <label className="text-sm text-gray-900">{t('country')}</label>
        <Select
          defaultValue="ALL"
          className="!w-[200px]"
          placeholder={t('country')}
          options={[
            { label: t('all'), value: 'ALL' },
            ...(data?.dashboardMetricFilterData.countries.map((country) => ({
              label: country.name,
              value: country.id,
            })) || []),
          ]}
          onChange={(value) => onChangeFilter({ countryId: value })}
        />
      </div>
    </div>
  );
}

export default MetricsDashboardFilters;
